import React, { useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import CancelButton from '../../../shared/CancelButton';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { QuickBooksItemPicker, QuickBooksConnectionNotice, QuickBooksItemStatusPill } from '../../../shared/QuickBooksItemPicker';
import { askConfirm } from '../../../shared/AppDialogs';

// The Product form's QuickBooks card (issue #265): the QuickBooks Item this
// Product is linked to, and linking, changing or removing it. Each change
// is saved straight away, apart from the form's own Save. Items are only
// fetched from QuickBooks when the picker is opened.

const SMALL_PRIMARY = { padding: '5px 12px', border: 'none', borderRadius: 6, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500 };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

function ProductQuickBooksSection({ product, sectionStyle, sectionHeaderStyle, onChange }) {
  const { canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [link, setLink] = useState(product.quickbooks_link || null);
  // null = picker closed; otherwise what /api/quickbooks/products/items said.
  const [picker, setPicker] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const openPicker = async () => {
    setBusy(true);
    try {
      const res = await axios.get('/api/quickbooks/products/items');
      setPicker(res.data.data);
      setPicked(null);
    } catch (err) {
      showError(err, 'Couldn’t load QuickBooks Items');
    }
    setBusy(false);
  };

  const save = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const res = await axios.put(`/api/quickbooks/products/${product.id}`, { qbo_item_id: picked.id });
      setLink(res.data.data);
      setPicker(null);
      dismissError();
      if (onChange) onChange();
    } catch (err) {
      showError(err, 'Linking failed');
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!(await askConfirm(`Remove this Product’s QuickBooks match?\n\nIt’s linked to “${link.qbo_item_name}”. Nothing changes in QuickBooks.`, { confirmLabel: 'Remove', destructive: true }))) return;
    setBusy(true);
    try {
      await axios.delete(`/api/quickbooks/products/${product.id}`);
      setLink(null);
      dismissError();
      if (onChange) onChange();
    } catch (err) {
      showError(err, 'Removing the match failed');
    }
    setBusy(false);
  };

  return (
    <div style={sectionStyle}>
      <h4 style={sectionHeaderStyle}>QuickBooks</h4>
      {picker ? (
        picker.connection.status !== 'connected' ? (
          <>
            <QuickBooksConnectionNotice connection={picker.connection} />
            <div style={{ marginTop: 10 }}><CancelButton onClick={() => setPicker(null)} /></div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 640 }}>
            <div style={{ flex: 1 }}>
              <QuickBooksItemPicker items={picker.items} value={picked?.id} onChange={setPicked} autoFocus disabled={busy} />
            </div>
            <button type="button" onClick={save} disabled={!picked || busy} title="Links this Product to the chosen QuickBooks Item" style={{ ...SMALL_PRIMARY, opacity: !picked || busy ? 0.6 : 1, cursor: !picked || busy ? 'not-allowed' : 'pointer' }}>
              Link
            </button>
            <CancelButton onClick={() => setPicker(null)} disabled={busy} />
          </div>
        )
      ) : link ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 14 }}>
          <span>
            {link.qbo_item_name}
            {link.qbo_item_sku && <span style={{ color: '#666' }}> · {link.qbo_item_sku}</span>}
          </span>
          <QuickBooksItemStatusPill status={link.qbo_item_status} />
          {canUpdate && (
            <span style={{ display: 'inline-flex', gap: 12, marginLeft: 6 }}>
              <button type="button" onClick={openPicker} disabled={busy} title="Picks a different QuickBooks Item for this Product" style={LINK_BUTTON}>Change</button>
              <button type="button" onClick={remove} disabled={busy} title="Removes this Product’s QuickBooks match" style={{ ...LINK_BUTTON, color: '#b91c1c' }}>Remove</button>
            </span>
          )}
          {busy && <Loader2 size={14} style={{ animation: 'qb-spin 0.8s linear infinite' }} />}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
          <span style={{ color: '#888' }}>Not linked to a QuickBooks Item</span>
          {canUpdate && (
            <button type="button" onClick={openPicker} disabled={busy} title="Picks the QuickBooks Item for this Product" style={LINK_BUTTON}>Link to QuickBooks Item</button>
          )}
          {busy && <Loader2 size={14} style={{ animation: 'qb-spin 0.8s linear infinite' }} />}
        </div>
      )}
      <style>{'@keyframes qb-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

export default ProductQuickBooksSection;
